// The Board's place in every module's pages (ADR 0064 step B). A page files work through the Board ("Build a …", a
// card a tool makes, the project's open cards) without depending on the Fleet module above it: Fleet offers the
// Board's dialogs and calls, and the web's host provides them here at start (app/board.ts).
import type { components, paths } from '@cortex/api-client';
import { Sparkles, type LucideIcon } from 'lucide-react';
import * as React from 'react';

export type BoardCard = components['schemas']['BoardRequest'];
export type BoardKind = BoardCard['kind'];
export type BoardStatus = BoardCard['status'];
export type BoardList = paths['/api/board/requests']['get']['responses'][200]['content']['application/json'];

/** What a "Build a …" request opens the Board's dialog on. */
export interface BuildRequestInit {
  kind: BoardKind;
  /** The dialog's heading (default: the button's label). */
  heading?: string;
  target?: string;
  title?: string;
  details?: string;
  /** Where the request is asked (the Projects page's Project assistant / Change this project). */
  origin?: 'board' | 'project_assistant';
}

/** A card a page files on a project's Board. */
export interface NewBoardCard {
  kind: BoardKind;
  title: string;
  details?: string | null;
  status?: 'todo' | 'queued';
  branch?: string;
  target?: { name: string } | null;
  groupKey?: string | null;
  source?: { origin?: 'board' | 'project_assistant' | 'chat' | 'api' };
}

export interface ProjectAssistantProps {
  onClose(): void;
  /** Reached from another modal (the New Project chooser, the Board's New task): back returns there. */
  onBack?: () => void;
  initialTitle?: string;
  initialDetails?: string;
}

/** What the Fleet module offers the pages below it. */
export interface BoardPort {
  RequestDialog: React.ComponentType<{ init: BuildRequestInit; onClose(): void }>;
  ProjectAssistantDialog: React.ComponentType<ProjectAssistantProps>;
  create(slug: string, card: NewBoardCard): Promise<BoardCard>;
  list(q: { project?: string }): Promise<BoardList>;
}

/** The statuses a card ends in (the Board's columns Done, Failed and Cancelled). */
export const BOARD_TERMINAL: readonly BoardStatus[] = ['done', 'failed', 'cancelled'];

let port: BoardPort | null = null;
/** The host provides the Board once, at start. */
export function provideBoard(p: BoardPort): void {
  port = p;
}
function board(): BoardPort {
  if (!port) throw new Error('The Board is not provided to this page (the host provides it at start).');
  return port;
}

/** The Board's cards, as a page below the Fleet module files and reads them. */
export const boardCards = {
  create: (slug: string, card: NewBoardCard) => board().create(slug, card),
  list: (q: { project?: string }) => board().list(q),
  isOpen: (status: BoardStatus) => !BOARD_TERMINAL.includes(status),
};

/** The Board's request dialog, for a page that opens it from its own menu ("Build a change", "Report a bug"). */
export function BuildRequestDialog(p: { init: BuildRequestInit; onClose(): void }) {
  const Dialog = board().RequestDialog;
  return (
    <React.Suspense fallback={null}>
      <Dialog {...p} />
    </React.Suspense>
  );
}

/** The Project assistant's dialog (a new project from a description). */
export function ProjectAssistantDialog(p: ProjectAssistantProps) {
  const Dialog = board().ProjectAssistantDialog;
  return (
    <React.Suspense fallback={null}>
      <Dialog {...p} />
    </React.Suspense>
  );
}

/** A rail footer button (dashed, full width), a ghost button, or a primary one. */
export type BuildRequestLook = 'rail' | 'ghost' | 'primary';

const LOOK: Record<BuildRequestLook, string> = {
  rail: 'w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer',
  ghost:
    'inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 transition-colors',
  primary:
    'inline-flex items-center gap-1.5 h-9 px-4 rounded-[5px] border-0 cursor-pointer bg-nav-active text-white text-sm font-medium hover:bg-nav-active-dark transition-colors',
};

/** The page's "Build a …" button and its dialog. */
export function BuildRequestButton({
  label,
  testId,
  look = 'rail',
  icon: Icon = Sparkles,
  className = '',
  ...init
}: BuildRequestInit & {
  label: string;
  testId: string;
  look?: BuildRequestLook;
  icon?: LucideIcon;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        className={`${LOOK[look]} ${className}`.trim()}
      >
        <Icon size={14} /> {label}
      </button>
      {open && <BuildRequestDialog init={{ heading: label, ...init }} onClose={() => setOpen(false)} />}
    </>
  );
}
